import { GetParetoAcumulado } from "./Pareto";

export const NOMBRE_SIN_FAMILIA = "Sin familia";
export const NOMBRE_SIN_SUBFAMILIA = "Sin sub familia";
export const NOMBRE_SIN_SUBFAMILIA2 = "Sin sub familia 2";
export const NOMBRE_SIN_LINEA = "Sin línea";
export const NOMBRE_SIN_SUCURSAL = "Sin sucursal";
// Bucket para filas sin `idcliente` (no se descartan). Detalle en README.
export const ID_SIN_CLIENTE = "sin-cliente";
export const NOMBRE_SIN_CLIENTE = "Sin cliente";
export const NOMBRE_SIN_ASESOR = "Sin asesor";

// Campo de monto usado para sumar "ventas" en todo el módulo.
export const obtenerMontoVenta = (fila) =>
    Number(fila.total ?? 0);

// Campo de cantidad usado por el toggle Monto/Cantidad de la Pantalla 2.
export const obtenerCantidad = (fila) => Number(fila.cantidad) || 0;

// Función según la métrica elegida en el toggle Monto/Cantidad.
const obtenerValorPorMetrica = (metrica) =>
    metrica === "cantidad" ? obtenerCantidad : obtenerMontoVenta;

// Margen (importe - costo) / importe por año, para mostrar junto al % de
// variación en ChartFamiliasRefacciones (Ventas por Familia/Cliente/Vendedor).
const acumularImporteCosto = (entrada, fila, ejercicio) => {
    entrada._importe[ejercicio] = (entrada._importe[ejercicio] || 0) + Number(fila.importe || 0);
    entrada._costo[ejercicio] = (entrada._costo[ejercicio] || 0) + Number(fila.costo || 0);
};
const finalizarConMargenes = (entrada) => {
    const margenes = {};
    Object.keys(entrada._importe).forEach((anio) => {
        const importe = entrada._importe[anio] || 0;
        const costo = entrada._costo[anio] || 0;
        margenes[anio] = importe > 0 ? (importe - costo) / importe : 0;
    });
    return { id: entrada.id, etiqueta: entrada.etiqueta, valores: entrada.valores, margenes };
};

// Agrupa venta por Familia + año, para la card de ranking de Pantalla 1.
export const agruparVentasPorFamiliaYPeriodo = (detalle = []) => {
    const mapa = new Map();
    detalle.forEach((fila) => {
        const familia = (fila.familia || "").trim() || NOMBRE_SIN_FAMILIA;
        const ejercicio = String(fila.ejercicio);
        if (!mapa.has(familia)) {
            mapa.set(familia, { id: familia, etiqueta: familia, valores: {}, _importe: {}, _costo: {} });
        }
        const entrada = mapa.get(familia);
        entrada.valores[ejercicio] = (entrada.valores[ejercicio] || 0) + obtenerMontoVenta(fila);
        acumularImporteCosto(entrada, fila, ejercicio);
    });
    return Array.from(mapa.values()).map(finalizarConMargenes);
};

// Igual que `agruparVentasPorFamiliaYPeriodo`, agrupando por Vendedor/Asesor.
// Filas sin `fila.vendedor` van al bucket `NOMBRE_SIN_ASESOR` (no se descartan).
export const agruparVentasPorVendedorYPeriodo = (detalle = []) => {
    const mapa = new Map();
    detalle.forEach((fila) => {
        const vendedor = (fila.vendedor || "").trim() || NOMBRE_SIN_ASESOR;
        const ejercicio = String(fila.ejercicio);
        if (!mapa.has(vendedor)) {
            mapa.set(vendedor, { id: vendedor, etiqueta: vendedor, valores: {}, _importe: {}, _costo: {} });
        }
        const entrada = mapa.get(vendedor);
        entrada.valores[ejercicio] = (entrada.valores[ejercicio] || 0) + obtenerMontoVenta(fila);
        acumularImporteCosto(entrada, fila, ejercicio);
    });
    return Array.from(mapa.values()).map(finalizarConMargenes);
};

// Igual que `agruparVentasPorFamiliaYPeriodo`, agrupando por Cliente. Filas
// sin `idcliente` van al bucket `ID_SIN_CLIENTE` (antes se descartaban).
export const agruparVentasPorClienteYPeriodo = (detalle = []) => {

    const mapa = new Map();
    detalle.forEach((fila) => {
        const sinCliente = fila.idcliente === undefined || fila.idcliente === null;
        // String(): si el endpoint llega a mezclar tipos (123 vs "123") para
        // el mismo cliente, no se fragmenta en 2 entradas distintas.
        const idcliente = sinCliente ? ID_SIN_CLIENTE : String(fila.idcliente);
        const ejercicio = String(fila.ejercicio);
        if (!mapa.has(idcliente)) {
            mapa.set(idcliente, {
                id: idcliente,
                etiqueta: sinCliente ? NOMBRE_SIN_CLIENTE : fila.razon_social || String(fila.idcliente),
                valores: {},
                _importe: {},
                _costo: {},
            });
        }
        const entrada = mapa.get(idcliente);
        entrada.valores[ejercicio] = (entrada.valores[ejercicio] || 0) + obtenerMontoVenta(fila);
        acumularImporteCosto(entrada, fila, ejercicio);
    });
    return Array.from(mapa.values()).map(finalizarConMargenes);
};

// Agrupa y suma ventas, ordena ascendente (los que menos venden primero) y recorta a `limite`.
export const agruparTop10 = (filas = [], { campoAgrupacion, campoEtiqueta, limite = 10 }) => {
    const mapa = new Map();
    filas.forEach((fila) => {
        const clave = fila[campoAgrupacion];
        if (clave === undefined || clave === null || clave === "") return;
        if (!mapa.has(clave)) {
            mapa.set(clave, { id: clave, nombre: fila[campoEtiqueta] || String(clave), valor: 0 });
        }
        mapa.get(clave).valor += obtenerMontoVenta(fila);
    });
    return Array.from(mapa.values())
        .sort((a, b) => a.valor - b.valor)
        .slice(0, limite);
};

// KPIs del dashboard de Pantalla 1. Con `anioPrincipal` se acota a ese año; sin él, suma todo `detalle`.
export const calcularKpisDesdeDetalle = (detalle = [], anioPrincipal) => {
    const filas = anioPrincipal
        ? detalle.filter((fila) => String(fila.ejercicio) === String(anioPrincipal))
        : detalle;
    const totalGeneral = filas.reduce((acc, fila) => acc + obtenerMontoVenta(fila), 0);
    const clientesTotales = new Set(filas.map((fila) => fila.idcliente)).size;
    const familiasTotales = new Set(
        filas.map((fila) => (fila.familia || "").trim()).filter((familia) => familia !== "")
    ).size;
    return { totalGeneral, clientesTotales, familiasTotales };
};

// Filtro client-side del sidebar sobre "detalle" (Sucursal solo aplica desde Pantalla 2).
export const filtrarDetallePorSidebar = (detalle = [], sidebarFiltros = {}) => {
    let filas = detalle;

    if (sidebarFiltros.asesor && sidebarFiltros.asesor.length > 0) {
        filas = filas.filter((fila) => sidebarFiltros.asesor.includes(fila.vendedor));
    }

    if (sidebarFiltros.cliente && sidebarFiltros.cliente.length > 0) {
        filas = filas.filter((fila) => sidebarFiltros.cliente.includes(fila.idcliente));
    }

    if (sidebarFiltros.familia && sidebarFiltros.familia.length > 0) {
        const familias = sidebarFiltros.familia;
        filas = filas.filter((fila) =>
            familias.includes((fila.familia || "").trim() || NOMBRE_SIN_FAMILIA)
        );
    }

    if (sidebarFiltros.subFamilia && sidebarFiltros.subFamilia.length > 0) {
        const subFamilias = sidebarFiltros.subFamilia;
        filas = filas.filter((fila) =>
            subFamilias.includes((fila.subfamilia1 || "").trim() || NOMBRE_SIN_SUBFAMILIA)
        );
    }

    if (sidebarFiltros.subFamilia2 && sidebarFiltros.subFamilia2.length > 0) {
        const subFamilias2 = sidebarFiltros.subFamilia2;
        filas = filas.filter((fila) =>
            subFamilias2.includes((fila.subfamilia2 || "").trim() || NOMBRE_SIN_SUBFAMILIA2)
        );
    }

    if (sidebarFiltros.linea && sidebarFiltros.linea.length > 0) {
        const lineas = sidebarFiltros.linea;
        filas = filas.filter((fila) =>
            lineas.includes((fila.linea || "").trim() || NOMBRE_SIN_LINEA)
        );
    }

    if (sidebarFiltros.sucursal && sidebarFiltros.sucursal.length > 0) {
        const sucursales = sidebarFiltros.sucursal;
        filas = filas.filter((fila) =>
            sucursales.includes((fila.sucursal || "").trim() || NOMBRE_SIN_SUCURSAL)
        );
    }

    return filas;
};

// Opciones del filtro múltiple "Familia" del sidebar, calculadas sobre `detalle` real.
export const obtenerOpcionesFamiliaDesdeDetalle = (detalle = []) => {
    const nombres = new Set(
        detalle.map((fila) => (fila.familia || "").trim() || NOMBRE_SIN_FAMILIA)
    );
    return Array.from(nombres)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((nombre) => ({ value: nombre, label: nombre }));
};

// Opciones del filtro "Asesor" del sidebar; sin nombre amigable, label = código.
export const obtenerOpcionesAsesorDesdeDetalle = (detalle = []) => {
    const codigos = new Set(detalle.map((fila) => fila.vendedor).filter(Boolean));
    return Array.from(codigos)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((codigo) => ({ value: codigo, label: codigo }));
};

// Opciones del filtro "Cliente" del sidebar (`idcliente` como valor, `razon_social` como etiqueta).
export const obtenerOpcionesClienteDesdeDetalle = (detalle = []) => {
    const mapa = new Map();
    detalle.forEach((fila) => {
        if (fila.idcliente === undefined || fila.idcliente === null) return;
        if (!mapa.has(fila.idcliente)) {
            mapa.set(fila.idcliente, fila.razon_social || String(fila.idcliente));
        }
    });
    return Array.from(mapa.entries())
        .sort((a, b) => a[1].localeCompare(b[1], "es"))
        .map(([idcliente, razonSocial]) => ({ value: idcliente, label: razonSocial }));
};

// Opciones de Sub Familia / Sub Familia 2 del sidebar de Pantalla 1.
export const obtenerOpcionesSubFamiliaDesdeDetalle = (detalle = []) => {
    const nombres = new Set(
        detalle.map((fila) => (fila.subfamilia1 || "").trim() || NOMBRE_SIN_SUBFAMILIA)
    );
    return Array.from(nombres)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((nombre) => ({ value: nombre, label: nombre }));
};

export const obtenerOpcionesSubFamilia2DesdeDetalle = (detalle = []) => {
    const nombres = new Set(
        detalle.map((fila) => (fila.subfamilia2 || "").trim() || NOMBRE_SIN_SUBFAMILIA2)
    );
    return Array.from(nombres)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((nombre) => ({ value: nombre, label: nombre }));
};

// Opciones del filtro "Línea" del sidebar de Pantalla 1.
export const obtenerOpcionesLineaDesdeDetalle = (detalle = []) => {
    const nombres = new Set(
        detalle.map((fila) => (fila.linea || "").trim() || NOMBRE_SIN_LINEA)
    );
    return Array.from(nombres)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((nombre) => ({ value: nombre, label: nombre }));
};

// Opciones de Sucursal para Pantalla 2, calculadas sobre el `detalle` ya acotado a la Familia.
export const obtenerOpcionesSucursalDesdeDetalle = (detalle = []) => {
    const nombres = new Set(
        detalle.map((fila) => (fila.sucursal || "").trim() || NOMBRE_SIN_SUCURSAL)
    );
    return Array.from(nombres)
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((nombre) => ({ value: nombre, label: nombre }));
};

// ─── Pantalla 2 (Detalle por Familia) ──────────────────────────────────────

// Acota "detalle" a una sola Familia (la clickeada en Pantalla 1).
export const filtrarDetallePorFamilia = (detalle = [], nombreFamilia) => {
    if (!nombreFamilia) return detalle;
    return detalle.filter(
        (fila) => ((fila.familia || "").trim() || NOMBRE_SIN_FAMILIA) === nombreFamilia
    );
};

// Filtro propio de Pantalla 2 (Sub Familia, Sub Familia 2, Cliente, Asesor); cada campo es un solo valor ("TODOS" = sin filtrar).
export const filtrarDetalleFamiliaPorFiltros = (detalle = [], filtros = {}) => {
    let filas = detalle;

    if (filtros.subFamilia && filtros.subFamilia !== "TODOS") {
        filas = filas.filter(
            (fila) => ((fila.subfamilia1 || "").trim() || NOMBRE_SIN_SUBFAMILIA) === filtros.subFamilia
        );
    }

    if (filtros.subFamilia2 && filtros.subFamilia2 !== "TODOS") {
        filas = filas.filter(
            (fila) => ((fila.subfamilia2 || "").trim() || NOMBRE_SIN_SUBFAMILIA2) === filtros.subFamilia2
        );
    }

    if (filtros.cliente && filtros.cliente !== "TODOS") {
        filas = filas.filter((fila) => String(fila.idcliente) === String(filtros.cliente));
    }

    if (filtros.asesor && filtros.asesor !== "TODOS") {
        filas = filas.filter((fila) => fila.vendedor === filtros.asesor);
    }

    return filas;
};

// ─── Pantalla "Detalle por Vendedor" (mismo patrón que Pantalla 2) ─────────

// Acota "detalle" a un solo Vendedor (el clickeado en el tab "Ventas por
// Vendedor" de Pantalla 1). Mismo criterio que `filtrarDetallePorFamilia`;
// bucket `NOMBRE_SIN_ASESOR` para filas sin vendedor asignado.
export const filtrarDetallePorVendedor = (detalle = [], nombreVendedor) => {
    if (!nombreVendedor) return detalle;
    return detalle.filter(
        (fila) => ((fila.vendedor || "").trim() || NOMBRE_SIN_ASESOR) === nombreVendedor
    );
};

// Filtro propio de "Detalle por Vendedor" (Familia, Sub Familia, Sub
// Familia 2, Cliente); mismo patrón que `filtrarDetalleFamiliaPorFiltros`,
// pero con Familia en vez de Asesor (aquí el Vendedor ya es fijo: es la
// pantalla completa, no un campo de filtro).
export const filtrarDetalleVendedorPorFiltros = (detalle = [], filtros = {}) => {
    let filas = detalle;

    if (filtros.familia && filtros.familia !== "TODOS") {
        filas = filas.filter(
            (fila) => ((fila.familia || "").trim() || NOMBRE_SIN_FAMILIA) === filtros.familia
        );
    }

    if (filtros.subFamilia && filtros.subFamilia !== "TODOS") {
        filas = filas.filter(
            (fila) => ((fila.subfamilia1 || "").trim() || NOMBRE_SIN_SUBFAMILIA) === filtros.subFamilia
        );
    }

    if (filtros.subFamilia2 && filtros.subFamilia2 !== "TODOS") {
        filas = filas.filter(
            (fila) => ((fila.subfamilia2 || "").trim() || NOMBRE_SIN_SUBFAMILIA2) === filtros.subFamilia2
        );
    }

    if (filtros.cliente && filtros.cliente !== "TODOS") {
        filas = filas.filter((fila) => String(fila.idcliente) === String(filtros.cliente));
    }

    return filas;
};

// KPIs de Pantalla 2: Venta total, Facturas, Factura promedio, Meses
// activos, Margen y Repetitividad (mismos 5 KPIs que calcularKpisClienteDesdeDetalle,
// aquí con `metrica` para el toggle Monto/Cantidad de esta pantalla).
export const calcularKpisFamiliaDesdeDetalle = (detalle = [], anioPrincipal, metrica = "monto") => {
    const filas = anioPrincipal
        ? detalle.filter((fila) => String(fila.ejercicio) === String(anioPrincipal))
        : detalle;

    const obtenerValor = obtenerValorPorMetrica(metrica);
    const ventaTotal = filas.reduce((acc, fila) => acc + obtenerValor(fila), 0);
    const facturasTotales = new Set(
        filas
            .map((fila) => fila.factura)
            .filter((factura) => factura !== undefined && factura !== null && factura !== "")
    ).size;
    const facturaPromedio = facturasTotales > 0 ? ventaTotal / facturasTotales : 0;
    const mesesActivos = new Set(
        filas
            .filter((fila) => fila.fecha)
            .map((fila) => `${fila.ejercicio}-${fila.fecha.split("-")[1]}`)
    ).size;

    const visitasTotales = new Set(
        filas.filter((fila) => fila.fecha).map((fila) => fila.fecha)
    ).size;
    const repetitividad = mesesActivos > 0 ? visitasTotales / mesesActivos : 0;

    const importeTotal = filas.reduce((acc, fila) => acc + Number(fila.importe || 0), 0);
    const costoTotal = filas.reduce((acc, fila) => acc + Number(fila.costo || 0), 0);
    const margen = importeTotal > 0 ? (importeTotal - costoTotal) / importeTotal : 0;

    return {
        ventaTotal,
        facturasTotales,
        facturaPromedio,
        mesesActivos,
        margen,
        visitasTotales,
        repetitividad,
    };
};

// Los mismos 5 KPIs de arriba, uno por año en vez del acumulado (reutiliza
// calcularKpisFamiliaDesdeDetalle) — para "KPIs por año" de Pantalla 2,
// igual patrón que calcularKpisClientePorAnio de Pantalla 3.
export const calcularKpisFamiliaPorAnio = (detalle = [], anios = [], metrica = "monto") =>
    anios.map((anio) => ({ anio, ...calcularKpisFamiliaDesdeDetalle(detalle, anio, metrica) }));

// Venta por Cliente plana (sin año), para el Pareto de Pantalla 2.
export const agruparVentasPorClientePlano = (detalle = [], metrica = "monto") => {
    const obtenerValor = obtenerValorPorMetrica(metrica);
    const mapa = new Map();
    detalle.forEach((fila) => {
        const sinCliente = fila.idcliente === undefined || fila.idcliente === null;
        const idcliente = sinCliente ? ID_SIN_CLIENTE : String(fila.idcliente);
        if (!mapa.has(idcliente)) {
            mapa.set(idcliente, {
                id: idcliente,
                nombre: sinCliente ? NOMBRE_SIN_CLIENTE : fila.razon_social || String(fila.idcliente),
                valor: 0,
            });
        }
        mapa.get(idcliente).valor += obtenerValor(fila);
    });
    return Array.from(mapa.values());
};

// Venta por Número de Parte, desglosada por año (para ChartFamiliasRefacciones).
export const agruparVentasPorNumeroParteYPeriodo = (detalle = [], metrica = "monto") => {
    const obtenerValor = obtenerValorPorMetrica(metrica);
    const mapa = new Map();
    detalle.forEach((fila) => {
        if (fila.parte === undefined || fila.parte === null || fila.parte === "") return;
        const ejercicio = String(fila.ejercicio);
        if (!mapa.has(fila.parte)) {
            mapa.set(fila.parte, {
                id: fila.parte,
                etiqueta: fila.nombre_parte ? `${fila.parte} - ${fila.nombre_parte}` : fila.parte,
                valores: {},
            });
        }
        const entrada = mapa.get(fila.parte);
        entrada.valores[ejercicio] = (entrada.valores[ejercicio] || 0) + obtenerValor(fila);
    });
    return Array.from(mapa.values());
};

// Acota "detalle" a las partes seleccionadas (clic en "Venta por número de parte"); sin selección regresa `detalle` tal cual.
export const filtrarDetallePorPartes = (detalle = [], partes = []) => {
    if (!partes || partes.length === 0) return detalle;
    return detalle.filter((fila) => partes.includes(fila.parte));
};

// Series mensuales (una por año de `anios`) para la dispersión de venta mensual.
export const construirSeriesMensualesDesdeDetalle = (detalle = [], anios = [], metrica = "monto") => {
    const obtenerValor = obtenerValorPorMetrica(metrica);
    return anios.map((anio) => {
        const valores = new Array(12).fill(0);

        detalle.forEach((fila) => {
            const [anioFila, mes] = fila.fecha.split("-");

            if (String(anioFila) !== String(anio)) {
                return;
            }

            const mesIndice = Number(mes) - 1;

            if (mesIndice < 0 || mesIndice > 11) {
                return;
            }

            valores[mesIndice] += obtenerValor(fila);
        });

        return { anio, valores };
    });
};

// ─── Pantalla 3 (Detalle por Cliente / 360) ────────────────────────────────

// Acota "detalle" a un solo Cliente.
export const filtrarDetallePorCliente = (detalle = [], idCliente) => {
    if (idCliente === undefined || idCliente === null) return detalle;
    // Drill-down a "Sin cliente": busca por ausencia de `idcliente`.
    if (idCliente === ID_SIN_CLIENTE) {
        return detalle.filter((fila) => fila.idcliente === undefined || fila.idcliente === null);
    }
    return detalle.filter((fila) => String(fila.idcliente) === String(idCliente));
};

// KPIs de Pantalla 3: Venta total, Facturas, Factura promedio, Meses
// activos, Margen y Repetitividad.
export const calcularKpisClienteDesdeDetalle = (detalle = [], anioPrincipal) => {
    const filas = anioPrincipal
        ? detalle.filter((fila) => String(fila.ejercicio) === String(anioPrincipal))
        : detalle;

    const ventaTotal = filas.reduce((acc, fila) => acc + obtenerMontoVenta(fila), 0);
    const facturasTotales = new Set(
        filas
            .map((fila) => fila.factura)
            .filter((factura) => factura !== undefined && factura !== null && factura !== "")
    ).size;
    const facturaPromedio = facturasTotales > 0 ? ventaTotal / facturasTotales : 0;
    const mesesActivos = new Set(
        filas
            .filter((fila) => fila.fecha)
            .map((fila) => `${fila.ejercicio}-${fila.fecha.split("-")[1]}`)
    ).size;

    const visitasTotales = new Set(
        filas.filter((fila) => fila.fecha).map((fila) => fila.fecha)
    ).size;
    const repetitividad = mesesActivos > 0 ? visitasTotales / mesesActivos : 0;

    const importeTotal = filas.reduce((acc, fila) => acc + Number(fila.importe || 0), 0);
    const costoTotal = filas.reduce((acc, fila) => acc + Number(fila.costo || 0), 0);
    const margen = importeTotal > 0 ? (importeTotal - costoTotal) / importeTotal : 0;

    return {
        ventaTotal,
        facturasTotales,
        facturaPromedio,
        mesesActivos,
        margen,
        visitasTotales,
        repetitividad,
    };
};

// Venta total por año para "Comparativo Anual" de Pantalla 3 (siempre todos los años de comparación).
export const agruparVentaPorAnio = (detalle = [], anios = []) =>
    anios.map((anio) => ({
        anio,
        total: detalle
            .filter((fila) => String(fila.ejercicio) === String(anio))
            .reduce((acc, fila) => acc + obtenerMontoVenta(fila), 0),
    }));

// Los mismos 5 KPIs del header, uno por año en vez del acumulado (reutiliza calcularKpisClienteDesdeDetalle).
export const calcularKpisClientePorAnio = (detalle = [], anios = []) =>
    anios.map((anio) => ({ anio, ...calcularKpisClienteDesdeDetalle(detalle, anio) }));

// ─── Pantalla 1 (Dashboard principal) ──────────────────────────────────────

// "80/20 de Clientes por Año": separa a los Clientes de cada año en grupo
// "80" (vital few) y "20" (resto), vía GetParetoAcumulado. Detalle en README.
export const construirParetoClientesPorAnio = (datosClientes = [], anios = []) => {
    const filas = [];
    anios.forEach((anio) => {
        // Incluye Clientes con neto ≤ 0 (tuvieron movimientos ese año) para
        // reconciliar con el header (ver README).
        const clientesDelAnio = datosClientes
            .filter((item) => Object.prototype.hasOwnProperty.call(item.valores, anio))
            .map((item) => ({ id: item.id, nombre: item.etiqueta, valor: item.valores[anio] || 0 }));
        if (clientesDelAnio.length === 0) return;

        const paretoAcumulado = GetParetoAcumulado(clientesDelAnio, "valor", 80);
        const totalClientes = clientesDelAnio.length;

        [
            { grupo: "80", registros: paretoAcumulado.filter((item) => item.esClave) },
            { grupo: "20", registros: paretoAcumulado.filter((item) => !item.esClave) },
        ].forEach(({ grupo, registros }) => {
            filas.push({
                grupo,
                anio,
                clientes: registros.length,
                porcentajeClientes: (registros.length / totalClientes) * 100,
                monto: registros.reduce((acc, item) => acc + item.valor, 0),
                clientesIds: registros.map((item) => item.id),
            });
        });
    });
    return filas;
};

// Detalle de Familias del grupo Pareto elegido ("80"/"20"/"total"): acota
// `detalle` a los Clientes de ese grupo y agrupa por Familia + año.
// Detalle completo en README.
export const construirFamiliasPorGrupoParetoYAnio = (detalle = [], filasPareto = [], grupo, metrica = "monto") => {
    const obtenerValor = metrica === "clientes" ? null : obtenerValorPorMetrica(metrica);
    const mapa = new Map();

    filasPareto
        .filter((fila) => grupo === "total" || fila.grupo === grupo)
        .forEach(({ anio, clientesIds }) => {
            const idsGrupo = new Set(clientesIds);
            detalle.forEach((fila) => {
                if (String(fila.ejercicio) !== String(anio)) return;
                // Mismo sentinel/normalización que agruparVentasPorClienteYPeriodo, para casar contra clientesIds.
                const idclienteFila =
                    fila.idcliente === undefined || fila.idcliente === null ? ID_SIN_CLIENTE : String(fila.idcliente);
                if (!idsGrupo.has(idclienteFila)) return;

                const familia = (fila.familia || "").trim() || NOMBRE_SIN_FAMILIA;
                if (!mapa.has(familia)) {
                    mapa.set(familia, { id: familia, etiqueta: familia, valores: {}, clientesPorAnio: {} });
                }
                const entrada = mapa.get(familia);

                if (metrica === "clientes") {
                    if (!entrada.clientesPorAnio[anio]) entrada.clientesPorAnio[anio] = new Set();
                    entrada.clientesPorAnio[anio].add(idclienteFila);
                    entrada.valores[anio] = entrada.clientesPorAnio[anio].size;
                } else {
                    entrada.valores[anio] = (entrada.valores[anio] || 0) + obtenerValor(fila);
                }
            });
        });

    return Array.from(mapa.values()).map(({ clientesPorAnio, ...resto }) => resto);
};
